import { useCallback, useRef, useState, type RefObject } from 'react';

/**
 * L'HÔTE DU CANVAS, EN ÉTAT — PAS SEULEMENT EN `ref` (fondatrice, 19/09/2026 : « j'ai activé et
 * désactivé le preview […] et les images ne s'affichent plus »).
 *
 * `BuilderCanvas` ne se démonte PAS quand on bascule l'aperçu : c'est le même composant qui rend un
 * autre arbre. Le `<div data-aqx-page>` de l'édition, lui, disparaît puis revient — un AUTRE nœud. Or un
 * `RefObject` est stable : `useEffect(…, [rootRef])` ne se rejoue jamais. Résultat, après un aller-retour
 * par l'aperçu :
 *
 *  - le filet des images (`watchMediaRecovery`) observait encore l'ANCIEN div, détaché du document ; le
 *    canvas d'édition repartait sans une ligne de secours, donc la première image qui trébuche restait
 *    morte jusqu'au rechargement — exactement le symptôme décrit, et la 3e fois en trois jours ;
 *  - le moteur d'animations (`initReveals`) idem, et comme son ménage ne passait pas non plus, le drapeau
 *    `data-aqx-anim-armed` restait posé sur `<html>` : les sections animées revenaient à `opacity: 0`
 *    SANS personne pour les marquer arrivées. Elles ne se voyaient plus du tout.
 *
 * D'où le nœud tenu en ÉTAT : monter ou démonter l'hôte devient une dépendance d'effet ordinaire, le
 * ménage part au bon moment, et le branchement revient avec le nouveau div. Le `RefObject` reste rendu
 * pour ce qui le veut vraiment (les cadres de survol/sélection lisent l'offset pendant le rendu).
 */
export interface HostNode<T extends HTMLElement> {
  /** À passer à `ref={…}` : range le nœud dans le ref ET le publie comme état. */
  attach: (node: T | null) => void;
  /** Le nœud monté, ou `null` — utilisable comme dépendance d'effet. */
  node: T | null;
  /** Le même nœud, en `ref`, pour les lecteurs synchrones (cadres mesurés). */
  ref: RefObject<T | null>;
}

export function useHostNode<T extends HTMLElement>(): HostNode<T> {
  const ref = useRef<T | null>(null);
  const [node, setNode] = useState<T | null>(null);
  // Stable : React n'appelle donc le rappel qu'au montage et au démontage du nœud, jamais à chaque rendu.
  const attach = useCallback((next: T | null) => {
    ref.current = next;
    setNode(next);
  }, []);
  return { attach, node, ref };
}
